import type {
  IField,
  IValidation,
  IValidationRule,
  IValidationError,
} from './interfaces/MetadataInterfaces.js';

const normalizeValidationRules = (rules: unknown): IValidationRule[] => {
  if (!Array.isArray(rules)) {
    return [];
  }

  return rules
    .map((rule): IValidationRule | null => {
      if (typeof rule === 'string') {
        return {
          rule,
          errorMessage: '',
        };
      }

      if (rule && typeof rule === 'object') {
        const parsedRule =
          typeof (rule as { rule?: unknown }).rule === 'string'
            ? (rule as { rule: string }).rule
            : '';

        if (!parsedRule) {
          return null;
        }

        return {
          rule: parsedRule,
          errorMessage:
            typeof (rule as { errorMessage?: unknown }).errorMessage ===
            'string'
              ? (rule as { errorMessage: string }).errorMessage
              : '',
        };
      }

      return null;
    })
    .filter((rule): rule is IValidationRule => rule !== null);
};

const getRuleErrorMessage = (
  rule: IValidationRule | undefined,
  fallback: string
): string => {
  const message = rule?.errorMessage?.trim();
  return message ? message : fallback;
};

const normalizeRuleValue = (rule: string, prefix: string): string =>
  rule.replace(prefix, '').trim();

const resolveNumericRuleValue = (rawValue: string): number => {
  if (rawValue === 'currentYear') {
    return new Date().getFullYear();
  }

  return Number.parseFloat(rawValue);
};

const resolveDateRuleValue = (rawValue: string): Date => {
  if (rawValue === 'currentDate') {
    return new Date();
  }

  if (rawValue === 'currentYear') {
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, 11, 31, 23, 59, 59, 999);
  }

  return new Date(rawValue);
};

const validateRule = (
  value: string,
  rules: IValidationRule[],
  filed: string,
  fieldType: string
): string | undefined => {
  const stringValue =
    value === null || value === undefined ? '' : String(value);

  const neRule = rules.find((r) => r.rule === 'NE');
  const nltRuleForEmpty = rules.find((r) => r.rule.startsWith('NLT'));
  const stringMinAppliesToEmpty =
    Boolean(nltRuleForEmpty) && fieldType === 'String';

  if (neRule && !stringValue) {
    return getRuleErrorMessage(neRule, `${filed}  can not be empty`);
  }

  if (!stringValue && !neRule && !stringMinAppliesToEmpty) {
    return undefined;
  }

  const ngtRule = rules.find((r) => r.rule.startsWith('NGT'));
  if (ngtRule) {
    const rawMax = normalizeRuleValue(ngtRule.rule, 'NGT');
    const max = resolveNumericRuleValue(rawMax);
    if (fieldType === 'String') {
      if (stringValue.length > max) {
        return getRuleErrorMessage(
          ngtRule,
          `${filed} should not be longer than ${max} chars`
        );
      }
    } else if (fieldType === 'Number') {
      const numValue = Number.parseFloat(stringValue);
      if (!Number.isNaN(numValue) && numValue > max) {
        return getRuleErrorMessage(
          ngtRule,
          `${filed} should not be more than ${rawMax}`
        );
      }
    } else if (fieldType === 'Date') {
      const dateValue = new Date(stringValue);
      const maxDate = resolveDateRuleValue(rawMax);
      if (
        !Number.isNaN(dateValue.getTime()) &&
        !Number.isNaN(maxDate.getTime()) &&
        dateValue.getTime() > maxDate.getTime()
      ) {
        return getRuleErrorMessage(
          ngtRule,
          `${filed} should not be after ${rawMax}`
        );
      }
    }
  }
  const nltRule = rules.find((r) => r.rule.startsWith('NLT'));
  if (nltRule) {
    const rawMin = normalizeRuleValue(nltRule.rule, 'NLT');
    const min = resolveNumericRuleValue(rawMin);
    if (fieldType === 'String') {
      if (stringValue.length < min) {
        return getRuleErrorMessage(
          nltRule,
          `${filed} should not be shorter than ${min} chars`
        );
      }
    } else if (fieldType === 'Number') {
      const numValue = Number.parseFloat(stringValue);
      if (!Number.isNaN(numValue) && numValue < min) {
        return getRuleErrorMessage(
          nltRule,
          `${filed} should not be less than ${rawMin}`
        );
      }
    } else if (fieldType === 'Date') {
      const dateValue = new Date(stringValue);
      const minDate = resolveDateRuleValue(rawMin);
      if (
        !Number.isNaN(dateValue.getTime()) &&
        !Number.isNaN(minDate.getTime()) &&
        dateValue.getTime() < minDate.getTime()
      ) {
        return getRuleErrorMessage(
          nltRule,
          `${filed} should not be before ${rawMin}`
        );
      }
    }
  }
  const rgxRule = rules.find((r) => r.rule.startsWith('RGX'));
  if (rgxRule && stringValue) {
    const regex = new RegExp(rgxRule.rule.replace('RGX', ''));
    if (!regex.test(stringValue)) {
      return getRuleErrorMessage(
        rgxRule,
        `${filed} does not match the pattern(${regex.source})`
      );
    }
  }

  return undefined;
};

const findFieldValue = (
  entity: any,
  fieldName: string,
  otherInfoFields?: IField[]
): any => {
  if (entity && Object.prototype.hasOwnProperty.call(entity, fieldName)) {
    return entity[fieldName];
  }

  if (
    entity?.otherInfo &&
    Object.prototype.hasOwnProperty.call(entity.otherInfo, fieldName)
  ) {
    return entity.otherInfo[fieldName];
  }

  const field = otherInfoFields?.find((f) => f.name === fieldName);
  if (field?.parentName && entity) {
    const parentPanel = entity[field.parentName];
    if (parentPanel) {
      if (Array.isArray(parentPanel) && parentPanel.length > 0) {
        const firstEntry = parentPanel[0];
        if (
          firstEntry &&
          Object.prototype.hasOwnProperty.call(firstEntry, fieldName)
        ) {
          return firstEntry[fieldName];
        }
      } else if (typeof parentPanel === 'object' && parentPanel !== null) {
        if (Object.prototype.hasOwnProperty.call(parentPanel, fieldName)) {
          return parentPanel[fieldName];
        }
      }
    }
  }

  if (entity && typeof entity === 'object') {
    for (const key in entity) {
      if (Object.prototype.hasOwnProperty.call(entity, key)) {
        const nestedValue = findFieldValue(
          entity[key],
          fieldName,
          otherInfoFields
        );
        if (nestedValue !== undefined) {
          return nestedValue;
        }
      }
    }
  }

  return undefined;
};

export const validate = (
  entity: any,
  validations: IValidation[],
  otherInfoFields?: IField[]
): IValidationError[] => {
  const errors: IValidationError[] = [];
  if (!entity) {
    return errors;
  }
  for (const rule of validations ?? []) {
    const fieldName = rule.field;
    const field = otherInfoFields?.find((f) => f.name === fieldName);
    const value = findFieldValue(entity, fieldName, otherInfoFields);
    const normalizedRules = normalizeValidationRules(
      (rule as { rules?: unknown }).rules
    );

    if (value !== undefined) {
      const stringValue =
        value === null || value === undefined ? '' : String(value);
      const err = validateRule(
        stringValue,
        normalizedRules,
        rule.field,
        field ? field.fieldType : typeof value
      );
      if (err) {
        errors.push({ fieldName: fieldName.toString(), message: err });
      }
    } else {
      const field = otherInfoFields?.find((f) => f.name === fieldName);
      if (field) {
        const hasNERule = normalizedRules.some((r) => r.rule === 'NE');
        const hasStringMin = normalizedRules.some((r) =>
          r.rule.startsWith('NLT')
        );
        if (hasNERule || (hasStringMin && field.fieldType === 'String')) {
          const err = validateRule(
            '',
            normalizedRules,
            rule.field,
            field.fieldType
          );
          if (err) {
            errors.push({ fieldName: fieldName.toString(), message: err });
          }
        }
      }
    }
  }
  return errors;
};
